import { ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Menu,
  X,
  User as UserIcon,
  ChevronRight,
  ListChecks,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { WordmarkLogo, REKINDERLogo } from "@/components/brand-logo";
import { LanguageSwitcher } from "@/components/language-switcher";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { AuthDialog } from "./auth-dialog";
import { motion, AnimatePresence } from "framer-motion";

export function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [location, navigate] = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  // Signing out leaves pages that need an account (My listings, Messages)
  // showing "Sign in required"; go to the landing page instead.
  const { toast } = useToast();
  const signOut = async () => {
    try {
      await logout();
      navigate("/");
    } catch {
      toast({ title: t("nav.signOutFailed"), variant: "destructive" });
    }
  };
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  // The mobile menu clips overflow only while its height is animating —
  // once open, overflow is released so the language dropdown nested inside
  // it (which needs to render past the menu's own bottom edge) isn't
  // clipped by this ancestor.
  const [mobileMenuSettled, setMobileMenuSettled] = useState(false);

  // Browse is the app's core function — it must always be reachable from
  // the persistent header nav, not just from links embedded in the home
  // page's own content (which disappear once you're anywhere else).
  const marketingNavLinks = [
    { href: "/browse", label: t("nav.browseListings") },
    { href: "/how-it-works", label: t("nav.howItWorks") },
    { href: "/safety", label: t("nav.safety") },
    { href: "/faq", label: t("nav.faq") },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F2ED] text-[#1A3535] antialiased">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#1A3535] border-b border-[#E8DDCF]/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-8 lg:gap-4 xl:gap-8">

            {/* Logo */}
            <Link href="/" className="flex items-center gap-2 shrink-0">
              <img
                src={`${import.meta.env.BASE_URL}images/rekinder-mark-transparent-light.svg`}
                alt=""
                width={40}
                height={40}
                className="w-10 h-10"
                draggable={false}
              />
              <span style={{ fontFamily: "'DM Sans Variable', sans-serif", fontWeight: 500, fontSize: 22, letterSpacing: "-0.02em", color: "#E8DDCF", lineHeight: 1 }}>Rekinder</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-1 flex-1">
              {marketingNavLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`flex items-center gap-1.5 px-4 lg:px-3 xl:px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    location === link.href
                      ? "border-[#E8DDCF]/20 bg-[#E8DDCF]/10 text-[#E8DDCF]"
                      : "border-transparent text-[#E8DDCF]/85 hover:border-[#E8DDCF]/20 hover:text-[#E8DDCF]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop Auth + Download + Language */}
            <div className="hidden lg:flex items-center gap-2 shrink-0">
              <LanguageSwitcher onDark compact />
              <Link
                href="/download"
                className="bg-[#7EB6B6] hover:bg-[#6aa3a3] text-[#1A3535] px-5 lg:px-4 xl:px-5 py-2 rounded-full text-sm font-semibold transition-colors"
              >
                {t("nav.download")}
              </Link>
              {isAuthenticated && user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                    className="flex items-center gap-2.5 hover:bg-[#E8DDCF]/10 p-1.5 pr-4 rounded-full transition-colors border border-[#E8DDCF]/20 hover:border-[#E8DDCF]/40"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#7EB6B6] text-[#1A3535] flex items-center justify-center font-display font-bold uppercase text-sm overflow-hidden">
                      {user.avatarUrl ? (
                        <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                      ) : (
                        user.name.charAt(0)
                      )}
                    </div>
                    <span className="font-semibold text-sm text-[#E8DDCF]">{user.name.split(" ")[0]}</span>
                  </button>

                  <AnimatePresence>
                    {isProfileMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#DDD8D0] overflow-hidden py-2"
                      >
                        <Link
                          href="/my-listings"
                          onClick={() => setIsProfileMenuOpen(false)}
                          className="flex items-center gap-3 w-full px-4 py-3 hover:bg-[#F5F2ED] text-[#333333] font-medium text-sm transition-colors"
                        >
                          <ListChecks size={15} className="text-[#2D7A78]" />
                          {t("nav.myListings")}
                        </Link>
                        <div className="h-px bg-[#DDD8D0] mx-4 my-1" />
                        <button
                          onClick={() => { void signOut(); setIsProfileMenuOpen(false); }}
                          className="flex items-center gap-3 w-full text-left px-4 py-3 hover:bg-red-50 text-red-500 font-medium text-sm transition-colors"
                        >
                          {t("nav.signOut")}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <button
                  onClick={() => setIsAuthOpen(true)}
                  className="border border-[#E8DDCF]/40 hover:bg-[#E8DDCF]/10 text-[#E8DDCF] px-5 lg:px-4 xl:px-5 py-2 rounded-full text-sm font-semibold transition-colors"
                >
                  {t("nav.signIn")}
                </button>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden ml-auto p-2 text-[#E8DDCF]/85 hover:bg-[#E8DDCF]/10 rounded-xl transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              onAnimationStart={() => setMobileMenuSettled(false)}
              onAnimationComplete={() => setMobileMenuSettled(true)}
              className="lg:hidden border-t border-[#DDD8D0] bg-white"
              style={{ overflow: mobileMenuSettled ? "visible" : "hidden" }}
            >
              <div className="px-4 pt-5 pb-6 space-y-5">

                {/* Download CTA */}
                <Link
                  href="/download"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm bg-[#7EB6B6] text-[#1A3535]"
                >
                  {t("nav.downloadJoinAlpha")}
                </Link>

                {/* Divider */}
                <div className="h-px bg-[#DDD8D0]" />

                {/* Marketing links */}
                <div className="space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A3535]/40 px-1 mb-2">
                    {t("nav.discover")}
                  </p>
                  {marketingNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      href={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-colors ${
                        location === link.href ? "bg-[#2D7A78]/10 text-[#2D7A78]" : "text-[#1A3535]/70 hover:bg-[#F5F2ED]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>

                {/* Divider */}
                <div className="h-px bg-[#DDD8D0]" />

                {/* Auth */}
                {isAuthenticated && user ? (
                  <div className="space-y-1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A3535]/40 px-1 mb-2">
                      {user.name.split(" ")[0]}
                    </p>
                    <Link
                      href="/my-listings"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm text-[#1A3535]/70 hover:bg-[#F5F2ED] transition-colors"
                    >
                      <ListChecks size={16} className="text-[#2D7A78]" />
                      {t("nav.myListings")}
                    </Link>
                    <button
                      onClick={() => { void signOut(); setIsMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm text-red-500 hover:bg-red-50 transition-colors w-full text-left"
                    >
                      <UserIcon size={16} />
                      {t("nav.signOut")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setIsAuthOpen(true); setIsMobileMenuOpen(false); }}
                    className="w-full py-3 border border-[#DDD8D0] text-[#1A3535] font-semibold rounded-full text-sm hover:bg-[#F5F2ED] transition-colors"
                  >
                    {t("nav.signIn")}
                  </button>
                )}

                {/* Language switcher in mobile menu */}
                <div className="h-px bg-[#DDD8D0]" />
                <div className="px-1">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#1A3535]/40 mb-2">
                    {t("language.label")}
                  </p>
                  <LanguageSwitcher />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="bg-[#1A3535] py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div className="col-span-1 md:col-span-2">
              <div className="mb-4">
                <WordmarkLogo variant="light" iconHeight="h-8" />
              </div>
              <p className="text-[#E8DDCF]/60 text-sm max-w-sm">
                {t("footer.tagline")}
              </p>
            </div>
            <div>
              <h4 className="font-display font-semibold text-[#E8DDCF] text-sm mb-4">
                {t("footer.discoverHeading")}
              </h4>
              <ul className="space-y-2 text-sm text-[#E8DDCF]/60">
                <li><Link href="/how-it-works" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("nav.howItWorks")}</Link></li>
                <li><Link href="/safety" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("nav.safety")}</Link></li>
                <li><Link href="/faq" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("nav.faq")}</Link></li>
                <li><Link href="/support" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("nav.support", "Support")}</Link></li>
                <li><Link href="/download" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("nav.download")}</Link></li>
                <li><Link href="/terms" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("footer.terms")}</Link></li>
                <li><Link href="/privacy" className="hover:text-white transition-colors flex items-center gap-1"><ChevronRight size={12} />{t("footer.privacy")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center text-[#E8DDCF]/40 text-sm">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </div>
        </div>
      </footer>

      <AuthDialog open={isAuthOpen} onOpenChange={setIsAuthOpen} />
    </div>
  );
}
